import { Box, Image, Theme, Typography } from "@outschool/backpack";
import { Trans, useTranslation } from "@outschool/localization";
import { joinMembershipPath } from "@outschool/routes";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useNavigation } from "@outschool/ui-utils";
import React from "react";

import Calendar from "../../../images/homepage/home_hero/calendar.webp";
import HexagonCircle from "../../../images/homepage/home_hero/hexagon-circle.webp";
import OrangeStar from "../../../images/homepage/home_hero/orange-star.webp";
import WhyOutschoolDoodleBottomRight from "../../../images/homepage/home_hero/why-outschool-doodle-bottom-right.webp";
import WhyOutschoolDoodleTopLeft from "../../../images/homepage/home_hero/why-outschool-doodle-top-left.webp";
import WhyOutschoolSquiggle from "../../../images/homepage/home_hero/why-outschool-squiggle.webp";

export const WhyChooseOutschool = React.forwardRef(
  (props: {}, ref: React.Ref<any>) => {
    const { t } = useTranslation("client\\components\\NewUserHome");
    const navigation = useNavigation();
    const items = [
      {
        title: t`Built for your kid’s unique needs`,
        description: t`Choose from 1000s of classes, from core academics to enrichment, supported by our concierge service.`,
        icon: HexagonCircle
      },
      {
        title: t`Live classes on your schedule`,
        description: t`Get personalized recommendations  that fit your daily life by adding your kid’s specific learning needs.`,
        icon: Calendar
      },
      {
        title: t`Flexible plans for every family`,
        description: t`Plans designed to grow with you. Change anytime, and unused credits roll over each month.`,
        icon: OrangeStar
      }
    ];
    return (
      <Box
        ref={ref}
        sx={(theme: Theme) => ({
          backgroundColor: theme.palette.primary[600],
          marginX: "auto",
          position: "relative",
          paddingBottom: "8.5em",
          paddingTop: "7em",
          [theme.breakpoints.down("md")]: {
            paddingTop: "4em",
            paddingBottom: "4em"
          }
        })}
      >
        <Box
          sx={(theme: Theme) => ({
            position: "absolute",
            top: "-111px",
            right: "0",
            [theme.breakpoints.down("md")]: { display: "none" }
          })}
        >
          <Image
            src={WhyOutschoolSquiggle}
            alt={t`Why choose Outschool?`}
            width={119}
            height={243}
          />
        </Box>
        <Box sx={{ maxWidth: "806px", marginX: "auto", paddingBottom: "4em" }}>
          <Typography
            variant="display1"
            sx={(theme: Theme) => ({
              color: theme.palette.common.white,
              textAlign: "center"
            })}
          >
            <Trans t={t}>Why choose Outschool?</Trans>
          </Typography>
        </Box>
        <Box>
          <Box
            sx={(theme: Theme) => ({
              position: "relative",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "1em",
              [theme.breakpoints.down("md")]: {
                flexDirection: "column",
                alignItems: "center",
                paddingX: "1em"
              }
            })}
          >
            {items.map((item, idx) => (
              <Box key={idx} sx={{ position: "relative" }}>
                {idx === 0 && (
                  <Box
                    sx={(theme: Theme) => ({
                      display: "none",
                      [theme.breakpoints.up("lg")]: {
                        display: "block",
                        position: "absolute",
                        top: "-82px",
                        left: "-140px",
                        zIndex: 0
                      }
                    })}
                  >
                    <Image
                      src={WhyOutschoolDoodleTopLeft}
                      alt={t`Scribble`}
                      width={349}
                      height={344}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    backgroundColor: "common.white",
                    borderRadius: "32px",
                    maxWidth: "360px",
                    maxheight: "350px",
                    paddingX: "2em",
                    position: "relative",
                    zIndex: 1
                  }}
                >
                  <Box
                    sx={{
                      marginX: "auto",
                      maxWidth: "300px",
                      paddingTop: "2em",
                      paddingBottom: "4em"
                    }}
                  >
                    <Box sx={{ marginBottom: "2rem" }}>
                      <Image
                        src={item.icon}
                        alt={item.title}
                        width={82}
                        height={82}
                      />
                    </Box>
                    <Typography
                      variant="display3"
                      sx={{ marginBottom: "1rem" }}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="body1">{item.description}</Typography>
                  </Box>
                </Box>
                {idx === items.length - 1 && (
                  <Box
                    sx={(theme: Theme) => ({
                      display: "none",
                      [theme.breakpoints.up("lg")]: {
                        display: "block",
                        position: "absolute",
                        bottom: "-87px",
                        right: "-56px",
                        zIndex: 0
                      }
                    })}
                  >
                    <Image
                      src={WhyOutschoolDoodleBottomRight}
                      alt={t`Scribble`}
                      width={148}
                      height={174}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{ display: "flex", justifyContent: "center", paddingTop: "3em" }}
        >
          <TrackedButton
            variant="contained"
            trackingName={"why_outschool_get_started"}
            onClick={() => navigation(joinMembershipPath(), { hardNav: true })}
            sx={{
              paddingY: "1.5rem"
            }}
          >
            <Trans t={t}>Get Started</Trans>
          </TrackedButton>
        </Box>
      </Box>
    );
  }
);
